import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useApiClient } from "../../api/ApiProvider";
import { useAuth } from "../../auth/AuthContext";
import { queryKeys, useEnvironments, useHealthMatrix, useNodes, useProjects } from "../../api/queries";
import type { Deployment, HealthCell, Placement, Project, Service } from "../../api/types";

/** A service row's rolled-up health, derived CLIENT-SIDE from the fleet health matrix
 * (charter A-5 — never a second, hand-set copy of what the matrix already says). `"healthy"`
 * means every placement cell for this service reports `health: "running"`; `"degraded"`
 * means at least one does not; `"unknown"` means the service has no health cell yet (not yet
 * placed, or the matrix hasn't observed it) — genuinely different from "degraded" and never
 * painted with the same alarm tone (A-10: an honest absence is not a problem to flag). */
export type OperateServiceHealth = "healthy" | "degraded" | "unknown";

/**
 * One row of the Operate landing view (IA slice 4,
 * docs/product/ui-information-architecture.md §3): a service owned by the signed-in user,
 * its project, the placements currently serving it, and the facets the OperateServices board
 * calls for — environment, rolled-up health, and the most recent deployment (its digest is
 * the "running revision," its `createdAt`/`createdBy` are "last deploy"). Every facet here is
 * DERIVED from a real read (env from the node the placement runs on, health from the fleet
 * health matrix, revision from the deploy timeline) — never a second, hand-maintained copy.
 */
export interface MyServiceRow {
  project: Project;
  service: Service;
  placements: Placement[];
  /** The environment the service's first placement runs in, when resolvable — a service
   * with no placement, or one whose node carries no environment binding, has none (render
   * "—", never a guessed default; A-10). A service placed across more than one environment
   * (not the common case) shows the first placement's — the same "one representative value"
   * simplification `EnvironmentBadge` callers elsewhere in this app already make. */
  environmentName: string | undefined;
  environmentClass: string | undefined;
  health: OperateServiceHealth;
  /** The first non-running health cell's server-supplied detail, when the row is degraded —
   * feeds the attention banner's explanation. Undefined for a healthy/unknown row. */
  healthDetail: string | undefined;
  /** How many of the service's observed placement cells are NOT healthy, and how many total
   * — feeds the attention banner's "N of M placements" copy. Both `0` when the service has
   * no health cells yet (an "unknown" row — see `health`). */
  healthUnhealthyCount: number;
  healthTotalCount: number;
  /** The newest deployment attempt for this service (any status), when one exists — its
   * `digest` is the "Running revision" column, its `createdAt`/`createdBy` are "Last
   * deploy." Absent for a service that has never been deployed through this path (e.g. one
   * still only configured, or a fresh adopt). */
  latestDeployment: Deployment | undefined;
}

export interface MyServiceRowsResult {
  rows: MyServiceRow[];
  isPending: boolean;
  isError: boolean;
  error: unknown;
  /**
   * True when the owned services themselves loaded fine but resolving placements,
   * environments, health, or the latest deployment for one or more of them failed — every
   * affected column degrades gracefully (falls back to its honest "unknown"/"—" rendering)
   * rather than failing the whole page, mirroring `useRouteRows`'s `placementsDegraded`
   * precedent.
   */
  placementsDegraded: boolean;
  refetch: () => void;
}

/** Capped fan-out, matching `useRouteRows`/`useHealthRows`'s documented
 * pre-alpha N+1: one page of projects, one page of services per project. */
const FAN_OUT_LIMIT = 100;

/**
 * Fans out from the fleet's projects to every project's services (exactly
 * the `useProjects` -> per-project `listServices` pattern `useRouteRows` and
 * `useHealthRows` already establish), passing the signed-in user's id as the
 * server-side `?createdBy=` filter (#408) so each per-project call already
 * comes back narrowed to services that user created — no client-side
 * `createdBy` match, no over-fetch.
 *
 * IMPORTANT — this filter is ADDITIVE, never a substitute for tenant/fleet
 * scoping: it is layered on the server's mandatory tenant+project scoping
 * (every list call in this app is already fleet-scoped), so this hook only
 * ever narrows *within* data the caller was already allowed to see. It must
 * never be the only thing standing between a user and another tenant's
 * services — that boundary is, and stays, the server's (S-8).
 *
 * Ownership identity: v0 ownership is `Service.createdBy` (charter brief for
 * #245 — no team entity, no membership). #280 settled what `createdBy` holds
 * on the real backend (PR #387): the STABLE PRINCIPAL ID — `caller.UserID`,
 * the immutable `usr_...` key — never a display name and never the mutable
 * username. The session DTO already exposes that same id as `session.userId`
 * (`api/client.ts`), so this hook filters on `createdBy: session.userId`.
 *
 * Filtering on the stable id, not `displayName`, is the point of #280:
 * display names are not unique, so a `displayName` match let two users who
 * happen to share one see each other's services in the filtered view — an
 * inaccuracy (never an access boundary; every call here is already
 * tenant-scoped). The opaque `userId` is unique per principal, so the filter
 * is accurate — and now applied server-side (#408), in the STORE query
 * itself, so pagination stays correct over the filtered set (P-6) instead of
 * over a page fetched-then-narrowed in the browser.
 *
 * IA slice 4 additions (Operate consolidation): environment (via a fleet-wide node fan-out,
 * the same `nodesById` precedent `DeploymentDetailModal` uses), rolled-up health (via the
 * fleet health matrix, the same source `HealthPage` reads), and the newest deployment (one
 * `listDeployments({ limit: 1 })` per owned service) — each a bounded, cached read layered
 * on the same owned-service set, none a second copy of server state (A-5).
 *
 * NOTE ON THE MOCK: `createMockApiClient` stamps `createdBy: "you"` on things
 * created through its `createProject`/`createService`/... calls — a human-facing
 * fiction that predates #387 and is independent of the real backend's id
 * semantics. A test therefore exercises this match by giving its cached session
 * a `userId` equal to the owned service's `createdBy` (see
 * `MyServicesPage.test.tsx`), which is what the real backend produces.
 */
export function useMyServiceRows(): MyServiceRowsResult {
  const client = useApiClient();
  const { session } = useAuth();
  const currentUser = session?.userId;

  const {
    data: projectsPage,
    isPending: projectsPending,
    isError: projectsIsError,
    error: projectsError,
    refetch: refetchProjects,
  } = useProjects({ limit: FAN_OUT_LIMIT });
  const projects = useMemo(() => projectsPage?.items ?? [], [projectsPage]);

  const serviceQueries = useQueries({
    queries: projects.map((project) => ({
      // `createdBy` gets its own key segment (mirrors `useContainers`'s
      // `unmanaged`/`serviceId` precedent) so the filtered page never
      // collides in cache with an unfiltered project-services list.
      queryKey: [...queryKeys.services(project.id), currentUser ?? null],
      queryFn: () => client.listServices({ projectId: project.id, createdBy: currentUser, limit: FAN_OUT_LIMIT }),
      // Nothing to ask the server for until we know whose services to filter to.
      enabled: Boolean(project.id) && Boolean(currentUser),
    })),
  });
  const servicesPending = serviceQueries.some((q) => q.isPending);
  const servicesIsError = serviceQueries.some((q) => q.isError);

  // Every entry the server sent back for this project already satisfies
  // `createdBy === currentUser` (the query above asked for exactly that) —
  // no client-side re-filter left, just the project/service pairing.
  const ownedEntries = useMemo(() => {
    const entries: { project: Project; service: Service }[] = [];
    if (!currentUser) return entries;
    projects.forEach((project, i) => {
      const services = serviceQueries[i]?.data?.items ?? [];
      services.forEach((service) => entries.push({ project, service }));
    });
    return entries;
  }, [projects, serviceQueries, currentUser]);

  const placementQueries = useQueries({
    queries: ownedEntries.map(({ project, service }) => ({
      queryKey: queryKeys.placements(project.id, service.id),
      queryFn: () =>
        client.listPlacements({ projectId: project.id, serviceId: service.id, limit: FAN_OUT_LIMIT }),
      enabled: true,
    })),
  });
  const placementsPending = placementQueries.some((q) => q.isPending);
  const placementsIsError = placementQueries.some((q) => q.isError);

  // The newest deployment per owned service — "Running revision"/"Last deploy" (board
  // columns). `limit: 1` since `listDeployments` is already newest-first (mirrors
  // `DeploymentTimeline`'s own ordering assumption).
  const deploymentQueries = useQueries({
    queries: ownedEntries.map(({ project, service }) => ({
      queryKey: [...queryKeys.deployments(project.id, service.id), "latest"],
      queryFn: () => client.listDeployments({ projectId: project.id, serviceId: service.id, limit: 1 }),
      enabled: true,
    })),
  });
  const deploymentsIsError = deploymentQueries.some((q) => q.isError);

  // Fleet-wide node fan-out (same bounded `limit: 100` precedent as
  // `useEnvironmentsById`/`DeploymentDetailModal`'s `nodesById`) — resolves a placement's
  // `nodeId` to its environment binding.
  const { data: nodesPage, isError: nodesIsError } = useNodes({ limit: FAN_OUT_LIMIT });
  const nodesById = useMemo(() => new Map((nodesPage?.items ?? []).map((n) => [n.id, n])), [nodesPage]);
  const { data: environmentsPage } = useEnvironments({ limit: FAN_OUT_LIMIT });
  const environmentsById = useMemo(
    () => new Map((environmentsPage?.items ?? []).map((e) => [e.id, e])),
    [environmentsPage],
  );

  // Fleet health matrix (same source `HealthPage`/`useHealthRows` reads) — rolled up
  // per-service below rather than a second, service-scoped health call per row.
  const { data: healthPage, isError: healthIsError } = useHealthMatrix({ limit: 200 });
  const healthCellsByService = useMemo(() => {
    const map = new Map<string, HealthCell[]>();
    for (const cell of healthPage?.items ?? []) {
      const existing = map.get(cell.serviceId);
      if (existing) existing.push(cell);
      else map.set(cell.serviceId, [cell]);
    }
    return map;
  }, [healthPage]);

  const rows: MyServiceRow[] = useMemo(
    () =>
      ownedEntries.map(({ project, service }, i) => {
        const placements = placementQueries[i]?.data?.items ?? [];
        const firstNode = placements[0] ? nodesById.get(placements[0].nodeId) : undefined;
        const environment = firstNode?.environmentId ? environmentsById.get(firstNode.environmentId) : undefined;

        const cells = healthCellsByService.get(service.id) ?? [];
        let health: OperateServiceHealth = "unknown";
        let healthDetail: string | undefined;
        const unhealthyCells = cells.filter((c) => c.health !== "running");
        if (cells.length > 0) {
          health = unhealthyCells.length > 0 ? "degraded" : "healthy";
          healthDetail = unhealthyCells[0]?.detail;
        }

        return {
          project,
          service,
          placements,
          environmentName: firstNode?.environmentName || undefined,
          environmentClass: environment?.class,
          health,
          healthDetail,
          healthUnhealthyCount: unhealthyCells.length,
          healthTotalCount: cells.length,
          latestDeployment: deploymentQueries[i]?.data?.items[0],
        };
      }),
    [ownedEntries, placementQueries, deploymentQueries, nodesById, environmentsById, healthCellsByService],
  );

  // Mirrors `useHealthRows`'s guard: an empty (or not-yet-known) owned set
  // must never trap the page in loading forever waiting on a placement
  // fan-out that has nothing to fan out over.
  const hasRows = ownedEntries.length > 0;
  const isPending = projectsPending || servicesPending || (hasRows && placementsPending);

  return {
    rows,
    isPending,
    isError: projectsIsError || servicesIsError,
    error: projectsError ?? serviceQueries.find((q) => q.error)?.error,
    placementsDegraded: placementsIsError || deploymentsIsError || nodesIsError || healthIsError,
    refetch: () => {
      void refetchProjects();
      serviceQueries.forEach((q) => void q.refetch());
      placementQueries.forEach((q) => void q.refetch());
      deploymentQueries.forEach((q) => void q.refetch());
    },
  };
}
